import { Renderer } from '@unseenco/taxi'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import * as browser from '../util/browserInfo'
import blockList from '../blocks/blockList'
import Component from '../components/Component'
import App from '../app'
import WebGLManager from '../webgl/WebGLManager'

/**
 * Model Layout
 *
 * Every other renderer should extend *Page* and implements its useful methods.
 *
 * @class
 * @augments Renderer
 */
export default class Layout extends Renderer {
  /**
   * Checks if the current browser is **Safari**.
   *
   * @private Called by `this.checkSafariElems()`.
   * @returns {void}
   */
  isSafari() {
    return browser.name === 'safari'
  }

  /**
   * Adds the `isSafari` css class to all elements with the `checkSafari` class only if the current browser IS **Safari**.
   *
   * @private Called by `this.onEnter()`.
   * @returns {void}
   */
  checkSafariElems() {
    if (!this.isSafari()) return

    const elems = document.querySelectorAll('.checkSafari')

    for (const elem of elems) elem.classList.add('isSafari')
  }

  initialLoad() {
    this.onEnter()
  }

  /**
   * Automatically called when a new page is added to the DOM.
   *
   * Initializes all blocks.
   *
   * Calls `this.bindMethods()`, `this.getElems()` and `this.events()` methods, in this specific order.
   *
   * @param {(Object|undefined)} props - An object with some data in it, will be stored in `this.props`.
   * @param {(PageGL|undefined)} props.webgl - A PageGL **CLASS** (not an instance) that will be linked to this Page.
   * @param {(Boolean|undefined)} props.gui - Whether this page must show a GUI folder.
   *
   * @public Called by `Taxi`.
   * @returns {void}
   */
  onEnter(props = {}) {
    this.view = this.content
    this.view.$page = this

    this.destroyed = false
    this.props = props
    this.elemAppear = [this.view.parentElement]

    this.checkSafariElems()

    this.blocks = []
    this.initBlocks()

    this.eventList = []
    this.tweens = []
    this.components = []

    this.bindMethods()
    this.getElems()

    const components = this.createComponents()

    if (components && Array.isArray(components)) {
      for (const component of components) this.addComponent(component)
    } else if (components && !Array.isArray(components)) this.addComponent(components)

    this.debugMode()
    this.events()
    App.checkWebGLForPage(this)
  }

  /**
   * Used to hide page content before it appears.
   *
   * @public Called by `App.js`.
   * @returns {void}
   */
  mask() {
  }

  /**
   * Starts the appear (IN) animation.
   *
   * Doing a fade in transition by default.
   *
   * You can override this method to create your own IN transition.
   *
   * Generally, you'll want to call `from.$page.destroy()` followed by `from.remove()` at the needed moment during your animation.
   *
   * @param {(Object|undefined)} params - An object containing transition parameters.
   * @param {(Element|undefined)} params.from - The source page element.
   * @param {Element} params.to - The destination page element.
   * @param {Object} params.trigger - The trigger of the page change.
   * @param {(Element|null)} params.canvas The canvas if the destination page has a PageGL.
   *
   * @public Called by `TransitionManager.js`.
   * @returns {Promise} A Promise resolved when the animation is completed.
   */
  appear({ from, to, trigger, canvas } = {}) {
    return new Promise((resolve) => {

      this.elemAppear.push(canvas)

      if (from) {
        from.$page.destroy()
        from.remove()

        gsap.fromTo(this.elemAppear, { alpha: 0 }, {
          duration: 1, delay: 0.5, ease: 'none', alpha: 1, onStart: () => {
            ScrollTrigger.refresh()
          }, onComplete: () => {
            resolve()
          }
        })
      } else {
        gsap.to(this.elemAppear, {
          duration: 1, ease: 'none', alpha: 1, onComplete: resolve
        })
      }
    })
  }

  /**
   * Starts the disappear (OUT) animation.
   *
   * Doing a fade out transition by default.
   *
   * You can override this method to create your own OUT transition.
   *
   * @param {Object} params - An object containing transition parameters.
   * @param {Element} params.from - The source page element.
   * @param {Object} params.trigger - The trigger of the page change.
   * @param {(Element|null)} params.canvas The canvas if the destination page has a PageGL.
   *
   * @public Called by `TransitionManager.js`.
   * @returns {Promise} A Promise resolved when the animation is completed.
   */
  disappear({ from, trigger, canvas }) {
    return new Promise((resolve) => {
      this.elemAppear.push(canvas)

      const tl = gsap.timeline({
        onComplete: () => {
          App.domScroll.scrollTo(0, 0)
          App.scrollY = 0
          App.S.scroll = 0
          App.S.targetScroll = 0
          App.S.contentNode.scrollTop = 0
          App.domScroll.scrollTop = 0
          App.domWrapper.scrollTop = 0
          App.S.scrollTo(0, { immediate: true })
          from.classList.add('!absolute', '!top-0')
          resolve()
        }
      })

      tl.fromTo(this.elemAppear, { alpha: 1 }, {
        duration: 0.5, alpha: 0
      }, 'start')
    })
  }

  /**
   * Here you can bind your methods to **this** instance.
   *
   * @abstract
   * @public Called by `this.onEnter()`.
   * @returns {void}
   */
  bindMethods() {
  }

  /**
   * Here you can get and store all the DOM elements needed by your page.
   *
   * @abstract
   * @public Called by `this.onEnter()`.
   * @returns {void}
   */
  getElems() {
  }

  /**
   * Create inner components.
   * It's important to initialize them in order (from top to bottom) in case they are using any ScrollTrigger.
   * Don't forget to return them in an array OR to call `this.addComponent()` to store them !
   *
   * @abstract
   * @returns {(void|Array<Component>)} An array of Components to be added to this Page.
   */
  createComponents() {
  }

  /**
   * Here you can add event listeners to all the DOM elements (stored earlier in `this.getElems()`) that need it.
   * Use `this.addEvent()` to register a new event.
   *
   * @abstract
   * @public Called by `this.onEnter()`.
   * @returns {void}
   */
  events() {
  }

  /**
   * Adds a new event to an element.
   * Stores the event to automatically remove it on page `destroy` method.
   *
   * @param {Object} options - The element to add the event to.
   * @param {(Element|Object|Array<Element|Object>)} options.target - The element/object to add the event to.
   * @param {String} options.name - The event name.
   * @param {Function} options.callback - The callback method called when the event is triggered.
   * @param {Boolean} [dom=true] - If `false`, the event will added using `.on` instead of `.addEventListener`.
   * @param {Boolean} [bind=this] - If given, the event callback will be bound to the value of `bind`. If the value is nullish, the callback will not
   *   be bound at all.
   *
   * @public
   * @returns {void}
   */
  addEvent({ target, name, callback, dom = true, bind = this } = {}) {
    if (!target || !name || !callback) return

    const fn = bind ? callback.bind(bind) : callback
    const targets = Array.isArray(target) ? target : [target]

    for (const el of targets) {
      if ((dom && !el.addEventListener) || (!dom && !el.on)) continue

      this.eventList.push({
        target: el, name, fn, dom, destroyed: false
      })

      if (dom) {
        el.addEventListener(name, fn)
      } else {
        el.on(name, fn)
      }
    }
  }

  /**
   * Stores a gsap tween/timeline/scrolltrigger to automatically kill it on page `destroy` method.
   *
   * @param {(Tween|Timeline|ScrollTrigger)} tween - The gsap tween (or a ScrollTrigger) to store.
   *
   * @public
   * @returns {void}
   */
  addTween(tween) {
    if (!tween || !tween.kill) return

    this.tweens.push(tween)
  }

  /**
   * Stores a Component instance in this Page, enabling all future calls to events (pointermove, resize, destroy, etc).
   *
   * @param {Component} component - A Component instance.
   *
   * @returns {void}
   */
  addComponent(component) {
    if (component instanceof Component) {
      this.components.push(component)

      if (!component.initiator) component.initiator = this
    }
  }

  prepareInit() {}

  /**
   * Prepare the page loading (Page and PageGL).
   *
   * @private Called by Transition.js
   * @returns {Promise} A Promise resolved when the assets have been loaded.
   */
  prepare() {
    return new Promise((resolve) => {
      const promises = [this.load()]

      if (this.pageGL) promises.push(this.pageGL.prepare())

      Promise.all(promises)
        .then(() => {
          if (this.pageGL) {
            gsap.delayedCall(0.15, resolve)
          } else {
            resolve()
          }
        })
    })
  }

  /**
   * Use this method to load assets if you need it.
   *
   * This method is called during a page transition, just before the new page shows.
   *
   * @public Called by `this.prepare()`.
   * @returns {Promise} A Promise resolved when the assets have been loaded.
   */
  load() {
    return Promise.resolve()
  }

  /**
   * Checks if a new PageGL needs to be initialized,
   * also checks if a PageGL is already running, and leaves it if needed.
   *
   * If no PageGL is linked to this page, disactivate the WebGL context.
   *
   * @private Called by `constructor`.
   * @returns {void}
   */
  checkWebGL() {
    if (!WebGLManager.instance) return

    // Gets the current PageGL (if any).
    const currentGL = WebGLManager.getCurrentPage()

    if (this.props.webgl) {
      // If a PageGL is linked to this Page,
      // Initialize it.
      // eslint-disable-next-line new-cap
      this.pageGL = new this.props.webgl({ page: this })

      // If the app already had a PageGL running and its name is not the same (= not the same template).
      if (currentGL && currentGL.name !== this.pageGL.name) {
        // Leave the last page.
        currentGL.onLeave()
        // Set the new page as the current page.
        WebGLManager.setCurrentPage(this.pageGL)
      }
    } else if (currentGL) {
      // If there isn't any PageGL linked to this page, AND if the app already had a PageGL running,
      // leave it.
      currentGL.onLeave()
      // Disactivates the WebGLManager.
      WebGLManager.disactivate()
    } else WebGLManager.disactivate(false)
  }

  /**
   * Initializes the instances of every found blocks in the page's view.
   *
   * @private Called by `this.onEnter()`.
   * @returns {void}
   */
  initBlocks() {
    if (!blockList || !blockList.length) return

    const classes = []

    for (const block of blockList) {
      if (block.name) {
        classes.push('.' + block.name)

        this.blocks.push({
          name: block.name, instances: []
        })
      }
    }

    if (!classes.length) return

    const els = this.view.querySelectorAll(classes.join(','))

    for (const el of els) {
      const block = blockList.find((blockData) => el.classList.contains(blockData.name))

      if (block) {
        const blockInstance = new block.Class(el)
        const instance = {
          el, class: blockInstance
        }
        const blPage = this.blocks.find((bl) => bl.name === block.name)

        blockInstance.initiator = this

        blPage.instances.push(instance)
      }
    }
  }

  /**
   * Automatically called when a new page has finished its **onEnter** animation.
   *
   * @public Called by `Highway`.
   * @returns {void}
   */
  onEnterCompleted() {
    for (const el of this.blocks) for (const block of el.instances) block.class.onEnterCompleted()

    for (const component of this.components) component.onEnterCompleted()
  }

  /**
   * Automatically called when very first page the user has visited just ended its **appear** animation.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  onFirstEnter() {
    for (const el of this.blocks) for (const block of el.instances) block.class.onEnterCompleted()
    for (const component of this.components) component.onEnterCompleted()
  }

  /**
   * Automatically called when very first page the user has visited just ended its **appear** animation.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  onFirstEnterCompleted() {
  }

  /**
   * Automatically called when the user navigates to another page.
   *
   * @public Called by `Highway`.
   * @returns {void}
   */
  onLeave() {
    if (this.guiFolder || this.gui) this.guiFolder ? this.guiFolder.dispose() : this.gui.dispose()
  }

  /**
   * Automatically called when the page has finished its **onLeave** animation.
   *
   * @public Called by `Highway`.
   * @returns {void}
   */
  onLeaveCompleted() {
  }

  /**
   * Called when the browser is resized.
   *
   * Calls the `resize()` method of every `Block` and `Component`.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  resize() {
    for (let i = 0; i < this.blocks.length; i++) {
      for (let j = 0; j < this.blocks[i].instances.length; j++) {
        this.blocks[i].instances[j].class.resize()
      }
    }

    for (const component of this.components) component.resize()
  }

  /**
   * Called when the browser is scrolling.
   *
   * Calls the `scroll()` method of every `Block`.
   *
   * Throttled to be called every 200ms.
   *
   * @param {Event} e - The scroll event.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  scroll(e) {
    for (let i = 0; i < this.blocks.length; i++) {
      for (let j = 0; j < this.blocks[i].instances.length; j++) {
        this.blocks[i].instances[j].class.scroll(e)
      }
    }

    for (const component of this.components) component.scroll(e)
  }

  /**
   * Called when the browser is scrolling.
   *
   * **NOTE**: this method is NOT throttled, **do not** use it for performance-sensitive operations, prefer `this.scrollThrottled()`.
   *
   * @param {Event} e - The scroll event.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  scrollNative(e) {
  }

  /**
   * Called when the browser has stopped scrolling.
   *
   * Debounced to be called 200ms after the last scroll event.
   *
   * @param {Event} e - The scroll event.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  scrollEnded(e) {
    for (let i = 0; i < this.blocks.length; i++) {
      for (let j = 0; j < this.blocks[i].instances.length; j++) {
        this.blocks[i].instances[j].class.scrollEnded(e)
      }
    }

    for (const component of this.components) component.scrollEnded(e)
  }

  /**
   * Called when the pointer is moving.
   *
   * Calls the `pointermove()` method of every `Block`.
   *
   * @param {PointerEvent} e - The pointermove event object.
   *
   * @public Called by `App`.
   * @returns {void}
   */
  pointermove(e) {
    for (let i = 0; i < this.blocks.length; i++) {
      for (let j = 0; j < this.blocks[i].instances.length; j++) {
        this.blocks[i].instances[j].class.pointermove(e)
      }
    }

    for (const component of this.components) component.pointermove(e)
  }

  touchmove(e) {
    for (let i = 0; i < this.blocks.length; i++) {
      for (let j = 0; j < this.blocks[i].instances.length; j++) {
        this.blocks[i].instances[j].class.touchmove(e)
      }
    }

    for (const component of this.components) component.touchmove(e)
  }

  /**
   * Called on every frame.
   *
   * Calls the `render()` method of every `Block`.
   *
   * @param {Number} time - The elapsed time since the first frame.
   * @param {Number} deltaTime - The delta between this frame and the last.
   *
   * @public Called by `App` using `gsap.ticker`.
   * @returns {void}
   */
  render(time, deltaTime) {
    if (this.blocks) {
      for (let i = 0; i < this.blocks.length; i++) {
        for (let j = 0; j < this.blocks[i].instances.length; j++) {
          this.blocks[i].instances[j].class.render(time, deltaTime)
        }
      }
    }

    if (this.components) {
      for (const component of this.components) component.render(time, deltaTime)
    }
  }

  debugMode() {
    if (window.DEV && window.DEV.gui && this.props.gui) window.DEV.gui.createPageGUI(this)
  }

  /**
   * Used to destroy any instanciated class, utility, remove any event, free any memory used.
   *
   * Automatically calls `destroy()` of every `Block`.
   * Automatically removes every event added via `this.addEvent()`.
   * Automatically kills every tween/timeline/scrolltrigger added via `this.addTween()`.
   *
   * @public Called by the Transition.
   * @returns {void}
   */
  destroy() {
    if (this.destroyed) return

    this.destroyed = true

    for (let i = 0; i < this.blocks.length; i++) {
      for (let j = 0; j < this.blocks[i].instances.length; j++) {
        this.blocks[i].instances[j].class.destroy()
      }
    }

    for (const component of this.components) component.destroy()

    for (const event of this.eventList) {
      (event.dom) ? event.target.removeEventListener(event.name, event.fn) : event.target.off(event.name, event.fn)

      event.destroyed = true
    }

    for (const tween of this.tweens) if (tween.kill) tween.kill()
  }
}
